import React, { useContext } from 'react';
import t from 'react-translate';
import { useSelector, useDispatch } from 'react-redux';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { css } from '@emotion/react';
import uuid from 'react-uuid';

// redux
import { getFlatCourseAliases } from 'redux/selectors/course';
import {
  createAiCharacterObjection,
  deleteAiCharacterObjection,
} from 'redux/actions/ai-role-play';

import { cardIconSize, doubleSpacing, halfSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import {
  ReactComponent as AICharacter,
} from 'styles/icons/ai-character-icon.svg';
import { gray5, primary } from 'styles/global_defaults/colors';
import ClickableContainer from 'components/clickable-container';
import {
  ReactComponent as AIPersona,
} from 'styles/icons/ai-persona.svg';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';
import NvIcon from 'shared/components/nv-icon';
import ObjectiveInputList from './objective-input-list';
import useAiRolePlayVoiceOptions from '../../hooks/use-ai-role-play-voice-options';
import useVoicePreview from '../../hooks/use-voice-preview';
import { AIPersonalities } from '..';
import EditAiInstructionContext from './context';
import AICharacterEditView from './ai-character-edit-modal';
import { getPersonalityLabel, getSelectedAvatarDisplayUrl, getVoiceName } from './ai-character-utils';

const styles = css`
  .learner-role-input {
    width: calc(50% - ${halfSpacing}px);
  }

  .ai-character-container {
    border: 1px solid ${gray5};
    border-radius: ${quarterSpacing}px;
    display: flex;
  }

  .action-btn {
    border: 1px solid ${primary} !important;
    border-radius: ${quarterSpacing}px;
  }

  .profile-image {
    width: ${cardIconSize}px;
    height: ${cardIconSize}px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

type CharacterSettingProps = {
  isEditView?: boolean;
  onEdit?: () => void;
  onBack?: () => void;
};

const CharacterSetting = (props: CharacterSettingProps) => {
  const { isEditView, onEdit, onBack } = props;
  const params = useLecturePageParams();
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));

  const {
    setValue,
    trigger,
    getValues,
    watch,
    control,
  } = useFormContext();
  const { aiRolePlayId } = useContext(EditAiInstructionContext) || {};
  const [isAddingNewAiObjective, setIsAddingNewAiObjective] = React.useState(false);
  const dispatch = useDispatch();

  const aiCharacterName = getValues('aiCharacterName');
  const aiCharacterRole = getValues('aiCharacterRole');
  const aiCharacterPersonality = getValues('aiCharacterPersonality');
  const aiCharacterFace = getValues('aiCharacterFace');
  const voiceOptionKey = watch('voiceOptionKey');
  const interactionMode = watch('interactionMode');
  const aiAvatarDisplayUrl = getSelectedAvatarDisplayUrl(aiCharacterFace);

  const isUnnamedCharacter = !aiCharacterName && !aiCharacterRole;
  const characterTitle = isUnnamedCharacter
    ? <i className='text-gray-2'>{t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.UNNAMED_CHARACTER()}</i>
    : (
      <>
        <strong>{aiCharacterName}</strong>
        {` (${aiCharacterRole})`}
      </>
    );

  const voiceOptions = useAiRolePlayVoiceOptions(params.catalogId);
  const selectedVoice = voiceOptions.find(v => v.key === voiceOptionKey);

  const { togglePreview, stopPreview } = useVoicePreview();

  React.useEffect(() => {
    stopPreview();
  }, [voiceOptionKey, stopPreview]);

  const isVoiceEnabled = interactionMode === RolePlayInteractionMode.VOICE;
  const genderKey = selectedVoice?.gender?.toUpperCase();

  const characterVoice = isVoiceEnabled && voiceOptionKey
    ? (
      <span>
        {getVoiceName(voiceOptionKey)}
        {genderKey && (<i> ({t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.GENDER[genderKey]()})</i>)}
      </span>
    )
    : (
      <i className='text-gray-2'>
        {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PERSONALITY_VOICE_NOT_ENABLED()}
      </i>
    );

  const displayPersonality = getPersonalityLabel(aiCharacterPersonality || AIPersonalities.NEUTRAL);
  const { fields, append } = useFieldArray({
    name: 'aiCharacterObjections',
    control,
    keyName: 'fieldId',
  });

  // We mutate control._defaultValues directly because RHF v7.22.1 doesn't support
  // shouldDirty: false on field array methods (append, remove) (added in v7.36.0). Public alternatives
  // like resetField reset both value and default (wiping pending text edits), and
  // reset(getValues(), { keepDirtyValues: true }) requires v7.31.0+. This is a
  // pragmatic tradeoff: it re-baselines defaults after already-saved structural
  // changes so isDirty reflects only unsaved text edits. Should be replaced with
  // native shouldDirty: false on append/remove if RHF is upgraded to v7.36.0+.
  const updateObjectionsDefaultValue = () => {
    const currentObjections = getValues('aiCharacterObjections') || [];
    // eslint-disable-next-line no-underscore-dangle
    control._defaultValues.aiCharacterObjections = currentObjections.map((obj) => ({ ...obj }));
  };

  const handleAddAiObjection = () => {
    if (aiRolePlayId) {
      setIsAddingNewAiObjective(true);
      dispatch(createAiCharacterObjection({
        catalogId: params.catalogId,
        aiRolePlayId,
        text: '',
      })).then((res) => {
        if (res) {
          // Use setValue instead of append to avoid marking the field as dirty,
          // since the objection is already saved to the backend
          const current = getValues('aiCharacterObjections') || [];
          setValue('aiCharacterObjections', [...current, { id: res.payload.id, text: '' }], { shouldDirty: false });
          updateObjectionsDefaultValue();
        }
      }).finally(() => {
        setIsAddingNewAiObjective(false);
      });
    } else {
      append({ id: uuid(), text: '' });
    }
  };

  const handleRemoveAiObjection = (aiCharacterObjectionId) => {
    if (fields.length <= 0) return;
    // Use setValue instead of remove to avoid marking the field as dirty,
    // since the deletion is already saved to the backend
    const current = getValues('aiCharacterObjections') || [];
    setValue(
      'aiCharacterObjections',
      current.filter((field) => field.id !== aiCharacterObjectionId),
      { shouldDirty: false },
    );
    updateObjectionsDefaultValue();
    if (aiRolePlayId && aiCharacterObjectionId) {
      dispatch(deleteAiCharacterObjection({
        catalogId: params.catalogId,
        aiRolePlayId,
        aiCharacterObjectionId,
      }));
    }
  };

  if (isEditView) {
    return (
      <AICharacterEditView
        onBackToRolePlay={onBack}
      />
    );
  }

  return (
    <div css={styles}>
      <div className='d-flex flex-column'>
        <div className='d-flex justify-content-center mt-8'>
          <AICharacter
            height={doubleSpacing}
            width={doubleSpacing}
          />
        </div>
        <p className='page-title text-large mb-5 mt-2 mx-auto text-black lh-35'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.THE_CHARACTERS()}
        </p>
      </div>
      <div>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER()}
        </p>
        <p className='text-large-body lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.CUSTOMIZE_AI_CHARACTER_TEXT()}
        </p>
        <div className='ai-character-container align-items-center p-4 justify-content-between'>
          <div className='d-flex align-items-center'>
            {aiAvatarDisplayUrl ? (
              <img
                src={aiAvatarDisplayUrl}
                alt={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PROFILE.AVATAR()}
                className='profile-image'
              />
            ) : (
              <AIPersona
                width={cardIconSize}
                height={cardIconSize}
                role='img'
                aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.AI_CHARACTER_AVATAR()}
              />
            )}
            <div className='ml-4'>
              <p className='mb-0 lh-25 text-large-body'>
                {characterTitle}
              </p>
              <p className='mb-0 lh-25 text-large-body'>
                <span className='font-weight-bolder'>
                  {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PERSONALITY_TEXT()}
                </span>
                {' '}
                {displayPersonality}
              </p>
              <p className='mb-0 lh-25 text-large-body'>
                <span className='font-weight-bolder'>
                  {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PERSONALITY_VOICE()}
                </span>
                {' '}
                {characterVoice}
              </p>
            </div>
          </div>
          <div className='d-flex align-items-center'>
            {isVoiceEnabled && selectedVoice && (
              <ClickableContainer
                className='action-btn d-flex align-items-center px-3 py-1 text-primary'
                onClick={() => selectedVoice && togglePreview(selectedVoice.key, selectedVoice.previewAssetPath)}
                aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.PREVIEW_VOICE()}
              >
                <NvIcon icon='audio' size='smallest' />
                <span className='text-regular font-weight-bold lh-25 pl-2'>
                  {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.PREVIEW_VOICE()}
                </span>
              </ClickableContainer>
            )}
            <ClickableContainer
              className='action-btn px-3 py-1 align-items-center ml-2 text-primary'
              onClick={onEdit}
              aria-label={t.FORM.EDIT()}
            >
              <NvIcon icon='edit' size='smallest' />
              <span className='text-regular lh-25 pl-2 font-weight-bold'>
                {t.FORM.EDIT()}
              </span>
            </ClickableContainer>
          </div>
        </div>
      </div>
      <div className='mt-5'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_OBJECTIONS()}
        </p>
        <p className='text-large-body mt-1 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.TELL_AI_TO_SAY()}
        </p>
        <ObjectiveInputList
          name='aiCharacterObjections'
          fields={fields}
          handleAddField={handleAddAiObjection}
          handleRemoveField={handleRemoveAiObjection}
          isAddingField={isAddingNewAiObjective}
          maxFieldCount={5}
        />
      </div>
      <div className='mt-5'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.LEARNER_ROLE(courseAliases)}
        </p>
        <p className='text-large-body mt-1 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.WHAT_ROLE_PLAYING()}
        </p>
        <NvTextInput
          withForm
          name='learnerRole'
          className='border-gray-5 bg-white mt-2 learner-role-input required-input'
          inputClassName='text-large-body lh-25'
          required
        />
      </div>
    </div>
  );
};

export default CharacterSetting;
